import { useEffect, useRef, type ReactElement } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { resolveUserFromToken } from './oidc';
import { useAuthStore } from './store';

/**
 * Has this tab already re-derived its claims? Module scope, not a ref, so it is
 * once per PAGE LOAD rather than once per mount — RequireAuth wraps every
 * authenticated route and remounts on navigation.
 */
let reconciled = false;

/**
 * Route gate: unauthenticated users are redirected to /login, remembering
 * where they were headed so sign-in can return them there.
 *
 * It also RE-DERIVES THE USER FROM THE TOKEN ONCE PER PAGE LOAD. The store
 * persists `user` under `solid-auth`, so a session that signed in yesterday
 * replays yesterday's claim parsing for ever — and `userFromClaims` used to be
 * called only in the login callback. When the org claim's key turned out to be
 * `org_id` rather than `id`, fixing the parser therefore fixed nothing for
 * anyone already signed in: their stored user kept an empty `orgs`, so
 * `activeOrgId` stayed null, so every query gated on `enabled: Boolean(orgId)`
 * never fired at all. The symptom is a screen of skeletons that never resolve
 * and no failed request to find, because no request was ever made.
 *
 * Signing out and back in would have fixed it. Requiring that of every user
 * after every claims change is not a fix, it is a support burden.
 */
export function RequireAuth({ children }: { children: ReactElement }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const accessToken = useAuthStore((s) => s.accessToken);
  const setUser = useAuthStore((s) => s.setUser);
  const location = useLocation();
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    if (reconciled || !isAuthenticated || !accessToken) return;
    reconciled = true;
    void resolveUserFromToken(accessToken)
      .then((user) => {
        // ⚠ An EMPTY answer is no information, never a replacement.
        // `resolveUserFromToken` swallows an unreachable or refusing userinfo
        // endpoint and returns a user built from no claims at all; writing that
        // over the signed-in user wiped the active organization, and every
        // query keyed on it (the recurring schedules, among others) disabled
        // itself and rendered an empty page. A dev token (`dev:…`) is not a
        // JWT and always takes that path.
        if (!user.email || (user.orgs ?? []).length === 0) return;
        // Only write when it actually changes something. An unconditional set
        // would notify every subscriber on every page load for no reason.
        const current = useAuthStore.getState().user;
        // ⚠ `apps` is part of the comparison: the rail renders from it, and a
        // session persisted before the pass-through fix carries narrowed
        // {clientId}-only entries. Comparing only email+orgs left those
        // sessions permanently unhealed — the fix shipped and nothing changed
        // for anyone already signed in, which is the userFromClaims lesson
        // repeating one field over.
        const appsKey = (list?: Array<Record<string, unknown>>) =>
          (list ?? []).map((a) => Object.keys(a).sort().join(":") + "=" + String(a.clientId)).join(",");
        const same =
          current?.email === user.email &&
          (current?.orgs ?? []).map((o) => o.id).join(",") === (user.orgs ?? []).map((o) => o.id).join(",") &&
          appsKey(current?.apps) === appsKey(user.apps);
        if (!cancelled.current && !same) setUser(user);
      })
      .catch(() => {
        // A claims refresh is best-effort. The token still works, the API still
        // answers, and failing here must never sign anybody out — that is the
        // same reasoning `lib/userinfo.ts` gives for answering 503 instead of
        // 401 when the IdP is unreachable.
        reconciled = false;
      });
    return () => {
      cancelled.current = true;
    };
  }, [isAuthenticated, accessToken, setUser]);

  if (!isAuthenticated) {
    return (
      <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
    );
  }
  return children;
}
